import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
    Code2, 
    Smartphone, 
    Palette, 
    Ship, 
    Clock, 
    Lightbulb,
    ArrowRight,
    Star,
    Users,
    Globe,
    Bitcoin,
    Shield,
    Award
} from 'lucide-react';

export const HomePage = () => {
    const services = [
        {
            icon: Code2,
            title: 'Custom Software Development',
            description: 'Scalable software solutions tailored to your business.',
        },
        {
            icon: Smartphone,
            title: 'App Development',
            description: 'Mobile and web applications designed for performance and usability.',
        },
        {
            icon: Palette,
            title: 'IT & Design Services',
            description: 'Professional IT consulting and design solutions for businesses.',
        },
        {
            icon: Ship,
            title: 'Export & Import Services',
            description: 'Global shipping of high-quality products with trust and reliability.',
        },
    ];

    const reasons = [
        {
            icon: Award,
            title: 'Professional Expertise',
            description: 'Experienced team delivering top-quality results.',
        },
        {
            icon: Clock,
            title: 'Reliable Delivery',
            description: 'On-time, trustworthy services worldwide.',
        },
        {
            icon: Lightbulb,
            title: 'Innovation & Technology',
            description: 'Cutting-edge solutions for IT and global business.',
        },
    ];

    const happyClients = [
        {
            name: 'John Smith',
            company: 'Tech Solutions Inc.',
            feedback: 'BIONEXX LLC delivered exceptional software that transformed our business operations. Highly recommended!',
            rating: 5,
        },
        {
            name: 'Sarah Johnson',
            company: 'Global Traders Co.',
            feedback: 'Their export services are top-notch. Reliable, professional, and always on time.',
            rating: 5,
        },
        {
            name: 'Michael Chen',
            company: 'StartUp Hub',
            feedback: 'The app they developed for us exceeded our expectations. Great team to work with!',
            rating: 5,
        },
    ];

    return (
        <main data-testid="home-page">
            {/* Hero Section with YouTube Video Background */}
            <section className="min-h-[90vh] flex items-center relative overflow-hidden bg-[#3C3B6E]" data-testid="hero-section">
                {/* YouTube Video Background */}
                <div className="absolute inset-0 w-full h-full overflow-hidden">
                    <iframe
                        className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 pointer-events-none scale-150"
                        src="https://www.youtube.com/embed/dx7CNovrg-A?autoplay=1&mute=1&loop=1&playlist=dx7CNovrg-A&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1"
                        title="American Flag Eagle Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#3C3B6E]/90 to-[#3C3B6E]/70"></div>
                
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="max-w-3xl">
                        <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-4 animate-fade-in">
                            Reliable IT & Export Solutions
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-tight mb-6 animate-fade-in-up">
                            BIONEXX LLC &ndash; Reliable IT & Export Solutions Worldwide
                        </h1>
                        <p className="text-lg text-white/80 mb-8 animate-fade-in-up animation-delay-100">
                            We deliver high-quality products and professional IT services to clients across the globe.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
                            <Link to="/contact">
                                <Button 
                                    size="lg"
                                    className="bg-[#B22234] hover:bg-[#8B1A1A] text-white rounded-md px-8 py-6 text-base font-semibold"
                                    data-testid="hero-cta-button"
                                >
                                    Contact Us Today
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>
                            </Link>
                            <Link to="/services">
                                <Button 
                                    size="lg"
                                    variant="outline"
                                    className="border-2 border-white text-white hover:bg-white hover:text-[#3C3B6E] rounded-md px-8 py-6 text-base font-semibold"
                                    data-testid="hero-services-button"
                                >
                                    Our Services
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Services Section */}
            <section className="py-20 lg:py-32 bg-white" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-left mb-12 lg:mb-16">
                        <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                            What We Offer
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-black text-[#3C3B6E] tracking-tight">
                            Key Services
                        </h2>
                    </div>

                    <div className="bento-grid">
                        {services.map((service, index) => (
                            <Card 
                                key={index}
                                className={`card-hover bg-gray-50 border border-gray-200 hover:border-[#B22234] ${index < 2 ? 'bento-large' : ''}`}
                                data-testid={`service-card-${index}`}
                            >
                                <CardContent className="p-6 lg:p-8">
                                    <div className="w-12 h-12 flex items-center justify-center bg-[#3C3B6E] rounded-lg mb-4">
                                        <service.icon className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-lg font-bold text-[#3C3B6E] mb-2 tracking-tight">
                                        {service.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        {service.description}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    <div className="mt-10 text-left">
                        <Link to="/services">
                            <Button 
                                variant="outline"
                                className="border-2 border-[#3C3B6E] text-[#3C3B6E] hover:bg-[#3C3B6E] hover:text-white rounded-md px-6"
                                data-testid="view-all-services-button"
                            >
                                View All Services
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="py-20 lg:py-32 bg-[#3C3B6E]" data-testid="why-choose-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-left mb-12 lg:mb-16">
                        <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                            Our Advantages
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                            Why Choose Us
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {reasons.map((reason, index) => (
                            <div 
                                key={index}
                                className="p-6 bg-white/10 border border-white/20 rounded-lg transition-all hover:border-[#B22234] hover:-translate-y-1"
                                data-testid={`reason-card-${index}`}
                            >
                                <div className="w-12 h-12 flex items-center justify-center bg-[#B22234] rounded-lg mb-4">
                                    <reason.icon className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2 tracking-tight">
                                    {reason.title}
                                </h3>
                                <p className="text-white/70 text-sm leading-relaxed">
                                    {reason.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Strategic Corporate Tie-ups Section */}
            <section className="py-20 lg:py-32 bg-white" data-testid="corporate-tieups-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                            Strategic Corporate Tie-ups
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-black text-[#3C3B6E] tracking-tight">
                            Global Partnerships
                        </h2>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 lg:p-10 text-center">
                        <p className="text-gray-700 text-base leading-relaxed max-w-3xl mx-auto">
                            BIONEXX LLC has established strong partnerships with global technology leaders and a strategic collaboration with a leading Chinese company &mdash; enabling us to deliver world-class technology solutions and international trade services.
                        </p>
                    </div>
                </div>
            </section>

            {/* Happy Clients Section */}
            <section className="py-20 lg:py-32 bg-white" data-testid="happy-clients-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <Users className="w-6 h-6 text-[#B22234]" />
                            <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em]">
                                Testimonials
                            </p>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-[#3C3B6E] tracking-tight">
                            Happy Clients
                        </h2>
                        <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                            See what our valued clients have to say about working with BIONEXX LLC
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {happyClients.map((client, index) => (
                            <Card 
                                key={index}
                                className="card-hover bg-gray-50 border border-gray-200 hover:border-[#B22234]"
                                data-testid={`client-card-${index}`}
                            >
                                <CardContent className="p-6 lg:p-8">
                                    <div className="flex gap-1 mb-4">
                                        {[...Array(client.rating)].map((_, i) => (
                                            <Star key={i} className="w-5 h-5 fill-[#B22234] text-[#B22234]" />
                                        ))}
                                    </div>
                                    <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">
                                        "{client.feedback}"
                                    </p>
                                    <div className="border-t border-gray-200 pt-4">
                                        <p className="font-bold text-[#3C3B6E]">{client.name}</p>
                                        <p className="text-gray-500 text-sm">{client.company}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Global Business Meetups Section */}
            <section className="py-20 lg:py-32 bg-gray-50" data-testid="global-meetups-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <Globe className="w-6 h-6 text-[#B22234]" />
                            <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em]">
                                Global Connections
                            </p>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-[#3C3B6E] tracking-tight">
                            Global Business Meetups
                        </h2>
                        <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                            Building partnerships with leading companies worldwide
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <Card className="bg-white border border-gray-200 hover:border-[#B22234] transition-all hover:-translate-y-1 text-center">
                            <CardContent className="p-6">
                                <h3 className="text-lg font-bold text-[#3C3B6E] mb-2">Trafigura</h3>
                                <p className="text-2xl mb-1">🇸🇬</p>
                                <p className="text-gray-500 text-sm">Singapore</p>
                                <p className="text-gray-600 text-sm mt-2">Commodities Trading</p>
                                <p className="text-[#B22234] font-bold mt-3">US$244 B+</p>
                            </CardContent>
                        </Card>

                        <Card className="bg-white border border-gray-200 hover:border-[#B22234] transition-all hover:-translate-y-1 text-center">
                            <CardContent className="p-6">
                                <h3 className="text-lg font-bold text-[#3C3B6E] mb-2">Tenaga Nasional Berhad</h3>
                                <p className="text-2xl mb-1">🇲🇾</p>
                                <p className="text-gray-500 text-sm">Malaysia</p>
                                <p className="text-gray-600 text-sm mt-2">Electricity Utility</p>
                                <p className="text-[#B22234] font-bold mt-3">US$16 B+</p>
                            </CardContent>
                        </Card>

                        <Card className="bg-white border border-gray-200 hover:border-[#B22234] transition-all hover:-translate-y-1 text-center">
                            <CardContent className="p-6">
                                <h3 className="text-lg font-bold text-[#3C3B6E] mb-2">Shoprite Holdings</h3>
                                <p className="text-2xl mb-1">🇿🇦</p>
                                <p className="text-gray-500 text-sm">South Africa</p>
                                <p className="text-gray-600 text-sm mt-2">Retail & Supermarkets</p>
                                <p className="text-[#B22234] font-bold mt-3">₹1.3 T+</p>
                            </CardContent>
                        </Card>

                        <Card className="bg-white border border-gray-200 hover:border-[#B22234] transition-all hover:-translate-y-1 text-center">
                            <CardContent className="p-6">
                                <h3 className="text-lg font-bold text-[#3C3B6E] mb-2">Emirates Airline</h3>
                                <p className="text-2xl mb-1">🇦🇪</p>
                                <p className="text-gray-500 text-sm">UAE</p>
                                <p className="text-gray-600 text-sm mt-2">Aviation</p>
                                <p className="text-[#B22234] font-bold mt-3">US$25 B+</p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Digital Assets & Cryptocurrency Section */}
            <section id="digital-assets" className="py-20 lg:py-32 bg-[#3C3B6E] relative overflow-hidden scroll-mt-24" data-testid="digital-assets-section">
                {/* Decorative Grid Background */}
                <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                    backgroundSize: '50px 50px'
                }}></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    {/* Header */}
                    <div className="text-center mb-12 lg:mb-16">
                        <div className="flex items-center justify-center gap-2 mb-3">
                            <Bitcoin className="w-6 h-6 text-[#B22234]" />
                            <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em]">
                                Technology & Innovation
                            </p>
                        </div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
                            Digital Assets & Cryptocurrency
                        </h2>
                        <h3 className="text-xl text-white/90 font-semibold mb-4">
                            Digital Assets & Cryptocurrency Technology
                        </h3>
                        <p className="text-white/80 text-base max-w-3xl mx-auto leading-relaxed">
                            Bionexx LLC is developing technology and digital solutions for the evolving digital-asset and cryptocurrency ecosystem. Our planned digital-asset platform is designed to support secure and user-friendly cryptocurrency services, including peer-to-peer (P2P) digital-asset transactions and related technology solutions.
                        </p>
                    </div>

                    {/* Crypto Visuals Gallery */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
                        <div className="rounded-lg overflow-hidden shadow-xl aspect-[4/3]">
                            <img 
                                src="https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=600&q=80" 
                                alt="Cryptocurrency and Bitcoin"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                        <div className="rounded-lg overflow-hidden shadow-xl aspect-[4/3]">
                            <img 
                                src="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=600&q=80" 
                                alt="Blockchain Technology"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                        <div className="rounded-lg overflow-hidden shadow-xl aspect-[4/3]">
                            <img 
                                src="https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=600&q=80" 
                                alt="Digital Wallet"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                        <div className="rounded-lg overflow-hidden shadow-xl aspect-[4/3]">
                            <img 
                                src="https://images.unsplash.com/photo-1639322537228-f710d846310a?w=600&q=80" 
                                alt="Secure Transaction Infrastructure"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    {/* Planned Platform Features */}
                    <div className="bg-white/5 border border-white/10 rounded-lg p-6 lg:p-10 mb-10">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 bg-[#B22234] rounded-lg flex items-center justify-center">
                                <Code2 className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-2xl font-black text-white">Planned Platform Features</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {[
                                'Peer-to-peer (P2P) digital-asset transactions',
                                'Cryptocurrency buying and selling functionality',
                                'Digital-asset wallet functionality',
                                'User account management',
                                'Identity verification and KYC integration',
                                'Transaction monitoring and compliance technology',
                                'Secure digital-asset transaction infrastructure',
                                'User-friendly web and mobile experiences',
                                'API and technology integrations',
                            ].map((feature, index) => (
                                <div key={index} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg border border-white/10 hover:border-[#B22234]/50 transition-all">
                                    <div className="w-6 h-6 flex-shrink-0 bg-[#B22234] rounded-full flex items-center justify-center mt-0.5">
                                        <span className="text-white text-xs font-bold">{index + 1}</span>
                                    </div>
                                    <p className="text-white/90 text-sm leading-relaxed">{feature}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Security & Compliance + Technology Focus */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
                        {/* Security & Compliance */}
                        <div className="bg-white/5 border border-white/10 rounded-lg p-6 lg:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-[#B22234] rounded-lg flex items-center justify-center">
                                    <Shield className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-xl font-black text-white">Security & Compliance</h3>
                            </div>
                            <p className="text-white/80 text-sm leading-relaxed mb-4">
                                Bionexx LLC intends to implement appropriate compliance and security measures for its digital-asset activities, including customer identification, KYC procedures, AML controls, sanctions screening, transaction monitoring, and applicable reporting requirements.
                            </p>
                            <p className="text-white/70 text-sm leading-relaxed italic">
                                Digital-asset services will be offered only where legally permitted and subject to applicable regulatory requirements.
                            </p>
                        </div>

                        {/* Technology Focus */}
                        <div className="bg-white/5 border border-white/10 rounded-lg p-6 lg:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-[#B22234] rounded-lg flex items-center justify-center">
                                    <Lightbulb className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-xl font-black text-white">Technology Focus</h3>
                            </div>
                            <p className="text-white/80 text-sm leading-relaxed mb-4">
                                Our technology approach focuses on security, transparency, reliability, user experience, and responsible digital-asset infrastructure.
                            </p>
                            <p className="text-white/70 text-sm leading-relaxed">
                                The platform is being developed with a focus on creating a secure environment for users while supporting applicable compliance requirements.
                            </p>
                        </div>
                    </div>

                    {/* Digital Asset Disclaimer */}
                    <div className="bg-[#1A1A3E] border-l-4 border-[#B22234] rounded-lg p-6 lg:p-8">
                        <div className="flex items-start gap-3 mb-3">
                            <div className="w-8 h-8 flex-shrink-0 bg-[#B22234] rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-bold">!</span>
                            </div>
                            <h3 className="text-lg font-black text-white">Digital Asset Disclaimer</h3>
                        </div>
                        <p className="text-white/80 text-sm leading-relaxed">
                            Cryptocurrency and digital-asset services are subject to applicable laws and regulations. Bionexx LLC does not represent that any particular service is available in every jurisdiction. Services will be launched and made available only after the applicable regulatory, compliance, technical, and operational requirements have been satisfied.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA Banner */}
            <section className="bg-[#B22234] py-16 lg:py-24" data-testid="cta-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-6">
                        Partner with BIONEXX LLC and take your business global.
                    </h2>
                    <Link to="/contact">
                        <Button 
                            size="lg"
                            className="bg-white text-[#B22234] hover:bg-gray-100 rounded-md px-8 py-6 text-base font-semibold"
                            data-testid="cta-quote-button"
                        >
                            Get a Quote
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </Link>
                </div>
            </section>
        </main>
    );
};
